import React, { useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../services/interceptor";
import CircularProgress from "@mui/material/CircularProgress";
import LeaveImportModal from "./Admin/LeaveImportModal";
import { toast } from "react-toastify";

// Columns matching Excel template exactly
const COLUMNS = [
  { id: "employeeId",        label: "Employee No.",     sortable: true,  align: "left"   },
  { id: "name",              label: "Employee Name",    sortable: true,  align: "left"   },
  { id: "leaveBalance",      label: "Leave Balance",    sortable: false, align: "center" },
  { id: "emergencyBalance",  label: "Emergency Balance",sortable: false, align: "center" },
];

function EmployeeLeaveList() {
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("employeeId");
  const [sortOrder, setSortOrder] = useState("asc");

  const {
    data: leaveEmployees,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "leaveEmployees",
      { page: currentPage, size: rowsPerPage, searchKeyword, sortBy, sortOrder },
    ],
    queryFn: async () => {
      const response = await axiosInstance.get("/user/leaveDetails", {
        params: {
          page: currentPage + 1,
          size: rowsPerPage,
          searchKeyword,
          sortBy,
          sortOrder,
        },
      });
      return response.data;
    },
  });

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const toggleSearch = () => {
    setSearchKeyword("");
    setSearchOpen(!searchOpen);
  };

  const handleSearchChange = (event) => {
    setSearchKeyword(event.target.value);
    setCurrentPage(0);
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
    setCurrentPage(0);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/leave/admin/import-balance/template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "leave_balance_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Template downloaded successfully");
    } catch (err) {
      toast.error("Failed to download template");
    }
  };

  const handleImportClose = () => {
    setImportModalOpen(false);
    refetch();
  };

  return (
    <div className="container mx-auto mt-8 p-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Employee Leave List</h2>
        <div className="flex gap-3">
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadTemplate}
            sx={{ textTransform: "none" }}
          >
            📥 Download Template
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => setImportModalOpen(true)}
            sx={{ textTransform: "none" }}
          >
            📤 Import Balance
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex justify-end gap-5 mr-8">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 48 48"
          id="search"
          onClick={toggleSearch}
          className="cursor-pointer"
        >
          <path d="M46.599 40.236L36.054 29.691C37.89 26.718 39 23.25 39 19.5 39 8.73 30.27 0 19.5 0S0 8.73 0 19.5 8.73 39 19.5 39c3.75 0 7.218-1.11 10.188-2.943l10.548 10.545a4.501 4.501 0 0 0 6.363-6.366zM19.5 33C12.045 33 6 26.955 6 19.5S12.045 6 19.5 6 33 12.045 33 19.5 26.955 33 19.5 33z"></path>
        </svg>
      </div>

      {searchOpen && (
        <div className="flex justify-end gap-5 mr-8 mt-2">
          <input
            type="text"
            placeholder="Search by Employee No. or Name..."
            value={searchKeyword}
            onChange={handleSearchChange}
            className="border border-gray-300 rounded-md px-2 py-1 w-64"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            className="cursor-pointer mt-1"
            onClick={toggleSearch}
          >
            <path
              fill="currentColor"
              d="M19.71 19.71c-.39.39-1.02.39-1.41 0L12 13.41l-6.29 6.3c-.39.39-1.02.39-1.41 0s-.39-1.02 0-1.41L10.59 12 4.3 5.71c-.39-.39-.39-1.02 0-1.41s1.02-.39 1.41 0L12 10.59l6.29-6.3c.39-.39 1.02-.39 1.41 0s.39 1.02 0 1.41L13.41 12l6.3 6.29c.39.39.39 1.03 0 1.42z"
            ></path>
          </svg>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center mt-10">
          <CircularProgress />
        </div>
      ) : isError ? (
        <div className="text-red-500 mt-4">Error: {error.message}</div>
      ) : (
        <>
          {leaveEmployees && leaveEmployees.items && leaveEmployees.items.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                        sortDirection={sortBy === col.id ? sortOrder : false}
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortOrder : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveEmployees.items.map((employee) => (
                    <TableRow key={employee.employeeId} hover>
                      <TableCell>{employee.employeeId}</TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell align="center">{employee.leaveBalance ?? 0}</TableCell>
                      <TableCell align="center">{employee.emergencyBalance ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="text-center mt-8 text-gray-500">No results found.</div>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 20]}
            component="div"
            count={leaveEmployees?.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </>
      )}

      <LeaveImportModal open={importModalOpen} onClose={handleImportClose} refetch={refetch} />
    </div>
  );
}

export default EmployeeLeaveList;
